import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Clock } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";

export function Learn() {
  const [guides, setGuides] = useState([]);
  useSeo({
    title: "Practical guides for workshops, yards and small fleets | Unity & Utility",
    description: "Free, practical guides and templates: fire drill records, extinguisher inspection logs, stock count sheets, driver walkaround checks, freight quote comparison and tool crib control.",
    path: "/learn",
    jsonLd: breadcrumbs([["Home", "/"], ["Guides", "/learn"]]),
  });

  useEffect(() => {
    api.get("/public-site/guides").then(({ data }) => setGuides(data.guides)).catch(() => setGuides([]));
  }, []);

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid="learn-page">
        <Crumbs items={[["Home", "/"], ["Guides", null]]} />
        <h1 className="font-display text-4xl font-bold tracking-tight text-[#0F172A] sm:text-5xl">
          Practical guides and templates
        </h1>
        <p className="mt-4 text-base leading-relaxed text-slate-600">
          The records an inspector asks for and the checks that keep a workshop running — written
          plainly, free to copy, no sign-up.
        </p>
        <div className="mt-8 space-y-3" data-testid="learn-list">
          {guides.map((g) => (
            <Link key={g.slug} to={`/learn/${g.slug}`} data-testid={`learn-card-${g.slug}`} className="block border border-border bg-white p-5 transition-shadow duration-200 hover:shadow-[0_2px_18px_rgba(15,23,42,0.07)]">
              <div className="text-sm font-semibold text-[#0F172A]">{g.title}</div>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{g.summary}</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500">
                <Clock size={13} /> {g.reading_minutes} min read
              </span>
            </Link>
          ))}
        </div>
      </div>
    </PublicShell>
  );
}

export default function Guide() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/public-site/guides/${slug}`)
      .then(({ data }) => setGuide(data))
      .catch(() => setMissing(true));
  }, [slug]);

  useSeo({
    title: guide ? `${guide.title} | Unity & Utility` : "Guide | Unity & Utility",
    description: guide?.summary,
    path: `/learn/${slug}`,
    jsonLd: guide ? {
      "@context": "https://schema.org",
      "@graph": [
        breadcrumbs([["Home", "/"], ["Guides", "/learn"], [guide.title, `/learn/${slug}`]]),
        {
          "@type": "Article", headline: guide.title, description: guide.summary,
          keywords: (guide.keywords || []).join(", "),
          author: { "@type": "Organization", name: "Unity & Utility" },
          publisher: { "@type": "Organization", name: "Unity & Utility" },
        },
      ],
    } : null,
  });

  if (missing) return <PublicShell><p data-testid="guide-missing" className="p-16 text-center text-sm text-slate-500">That guide does not exist.</p></PublicShell>;
  if (!guide) return <PublicShell><p className="p-16 text-center text-sm text-slate-500">Loading…</p></PublicShell>;

  return (
    <PublicShell>
      <article className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid={`guide-${slug}`}>
        <Crumbs items={[["Home", "/"], ["Guides", "/learn"], [guide.title, null]]} />
        <h1 data-testid="guide-h1" className="font-display text-4xl font-bold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl">
          {guide.title}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-slate-600">{guide.summary}</p>
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500">
          <Clock size={13} /> {guide.reading_minutes} min read
        </p>

        <div className="mt-9 space-y-7" data-testid="guide-body">
          {guide.body.map(([head, text]) => (
            <section key={head}>
              <h2 className="text-base font-semibold text-[#0F172A] md:text-lg">{head}</h2>
              {text.includes(" | ") ? (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full border border-border bg-white text-xs">
                    <thead className="bg-slate-50">
                      <tr>{text.split(" | ").map((c) => (
                        <th key={c} className="border-b border-border px-2 py-2 text-left font-semibold text-[#0F172A]">{c}</th>
                      ))}</tr>
                    </thead>
                    <tbody><tr>{text.split(" | ").map((c) => (
                      <td key={c} className="px-2 py-3 text-slate-400">—</td>
                    ))}</tr></tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-2 text-sm leading-relaxed text-slate-700">{text}</p>
              )}
            </section>
          ))}
        </div>

        <div className="mt-10 border-t border-border pt-5">
          <p className="text-xs text-slate-500">Share this guide</p>
          <div className="mt-2">
            <ShareBar url={`${window.location.origin}/learn/${slug}`} text={guide.title} testid="guide-share" />
          </div>
        </div>

        <div className="mt-10 border border-border bg-white p-6">
          <p className="text-sm leading-relaxed text-slate-600">
            Keep these records without the paperwork — Unity &amp; Utility logs drills, inspections
            and incidents, and flags what is overdue on its own.
          </p>
          <Link to="/solutions/safety" data-testid="guide-cta-solution" className="tap-safe mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#002FA7] hover:underline">
            See how it works <ArrowRight size={14} />
          </Link>
        </div>

        <h2 className="mt-12 text-base font-semibold text-[#0F172A] md:text-lg">Read next</h2>
        <ul className="mt-3 space-y-2" data-testid="guide-related">
          {(guide.related || []).map((r) => (
            <li key={r.slug}>
              <Link to={`/learn/${r.slug}`} data-testid={`guide-related-${r.slug}`} className="block border border-border bg-white px-4 py-3 text-sm text-slate-700 transition-colors duration-150 hover:border-[#002FA7] hover:text-[#002FA7]">
                {r.title}
              </Link>
            </li>
          ))}
        </ul>
      </article>
    </PublicShell>
  );
}
